import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CurrentMax, EffortScale, Exercise, NewProgram, UnitsPreference } from "@heraion/shared";

import { Dialog } from "../primitives/Dialog";
import { useLocale } from "../primitives/useLocale";
import { buildErrorText } from "../ProgramEditor/buildErrorText";
import { ModelWizard } from "../ProgramComposer/ModelWizard";
import { ProgramPreview } from "../Programs/ProgramPreview";
import type { UiTheme } from "../theme";
import { copyStatus, type LibraryItem } from "./libraryFilters";
import {
  buildModelFollow,
  followDefaults,
  followPreviewProgram,
  type FollowFilters,
} from "./modelCatalog";
import type { LibraryState } from "./useLibrary";

type ModelItem = Extract<LibraryItem, { kind: "model" }>;

// #472: Follow and Customise on the Library's catalog. Follow writes at once,
// a model at its defaults (followDefaults); Customise opens the model's
// widget, which writes nothing before its Follow. Every write goes through
// the one cloneItem. F3: the widget's Edit writes nothing either; it hands
// the program to the editor, whose first Save makes the copy.
export function useCatalogFollow(
  lib: Pick<LibraryState, "cloneItem" | "cloningKey" | "cloneError" | "authoredAs">,
  ctx: {
    /** The library's filters: a model starts on their level and days. */
    filters: FollowFilters;
    /** Not read yet: a model's Follow waits, or the copy would carry slugs
     *  as its exercise names. */
    catalog: ReadonlyArray<Exercise> | null | undefined;
    effortScale: EffortScale;
    /** The host's editor, opened on an unsaved program. Absent: no Edit. */
    onEdit?: (seed: NewProgram) => void;
  },
  /** After a durable follow. */
  onFollowed?: () => void,
) {
  const { t } = useTranslation();
  const lang = useLocale();
  const [open, setOpen] = useState<ModelItem | null>(null);
  // A model's defaults the build refused (#400): said where cloneError is.
  const [refused, setRefused] = useState<string | null>(null);
  const waiting = !ctx.catalog;
  const write = async (item: LibraryItem, built?: NewProgram) => {
    const ok = await lib.cloneItem(item, built);
    if (ok) onFollowed?.();
    return ok;
  };
  const follow = (item: LibraryItem) => {
    setRefused(null);
    if (item.kind !== "model") return void write(item);
    if (waiting) return;
    const built = buildModelFollow(item.model, followDefaults(item.model, ctx.filters), {
      catalog: ctx.catalog,
      effortScale: ctx.effortScale,
      lang,
    });
    if (built.ok) return void write(item, built.program);
    console.warn("[catalog follow] build failed:", built.reason);
    setRefused(buildErrorText(built.code, t));
  };
  const confirm = async (built: NewProgram) => {
    if (open && (await write(open, built))) setOpen(null);
  };
  // Born as Follow would write it: the status is copyStatus's.
  const hostEdit = ctx.onEdit;
  const edit =
    hostEdit && open
      ? (built: NewProgram) => {
          setOpen(null);
          hostEdit({ ...built, status: copyStatus(open, lib.authoredAs) });
        }
      : undefined;
  return {
    open,
    defaults: open ? followDefaults(open.model, ctx.filters) : null,
    busy: open !== null && lib.cloningKey === open.key,
    error: open ? lib.cloneError : null,
    /** The list's error line: the widget says its own. */
    listError: open ? null : (refused ?? lib.cloneError),
    waiting,
    follow,
    customise: (item: ModelItem) => {
      setRefused(null);
      setOpen(item);
    },
    confirm,
    edit,
    close: () => setOpen(null),
  };
}

export type CatalogFollow = ReturnType<typeof useCatalogFollow>;

type PreviewHost = {
  dark: boolean;
  unit: UnitsPreference;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  resolveName: (slug: string, snapshot?: string) => string;
};

/** The widget's preview: the program its Follow would write. */
function FollowPreview({
  theme,
  program,
  id,
  catalog,
  host,
}: {
  theme: UiTheme;
  program: NewProgram;
  id: string;
  catalog: Exercise[] | null;
  host: PreviewHost;
}) {
  const shown = useMemo(() => followPreviewProgram(program, id), [program, id]);
  return (
    <ProgramPreview
      program={shown}
      catalog={catalog}
      resolveName={host.resolveName}
      unit={host.unit}
      theme={theme}
      dark={host.dark}
      maxMeta={host.maxMeta}
    />
  );
}

/** Customise: the model's wizard with the axes that vary, and a preview. */
export function ModelFollowDialog({
  theme,
  state,
  catalog,
  effortScale,
  preview,
}: {
  theme: UiTheme;
  state: CatalogFollow;
  catalog: Exercise[] | null;
  effortScale: EffortScale;
  preview: PreviewHost;
}) {
  const { open, defaults, busy, error, waiting } = state;
  const id = open?.model.id;
  const renderPreview = useCallback(
    (program: NewProgram | null) =>
      program && id ? (
        <FollowPreview theme={theme} program={program} id={id} catalog={catalog} host={preview} />
      ) : null,
    [theme, id, catalog, preview],
  );
  if (!open || !defaults) return null;
  return (
    <Dialog
      theme={theme}
      visible
      // The write in flight: stay.
      onRequestClose={busy ? () => {} : state.close}
      dismissOnBackdropPress={!busy}
    >
      <ModelWizard
        theme={theme}
        model={open.model}
        catalog={catalog ?? undefined}
        effortScale={effortScale}
        follow={{ defaults, busy, error, waiting, renderPreview, ...(state.edit ? { onEdit: state.edit } : {}) }}
        onCreate={(built) => void state.confirm(built)}
        onBack={state.close}
      />
    </Dialog>
  );
}
